import {useMutation, useReactiveVar, useQuery} from "@apollo/client";
import { MeterDeliveryRequestForm, Modal } from "components";
import { useFormik } from "formik";
import { FC } from "react";
import toast from "react-hot-toast";
import { wrapClick } from "utils";
import { CREATE_METER_DELIVERY_REQUEST, GET_METER_CONTRACTOR } from "./queries";
import {currentUserVar} from "../../apollo/cache/auth";
import { useNavigate} from "react-location";

interface CreateMeterDeliveryRequestContainerProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}

const CreateMeterDeliveryRequestContainer: FC<CreateMeterDeliveryRequestContainerProps> = ({
  open,
  setOpen,
  refetch,
}) => {

  const navigate = useNavigate();
  const currentUser = useReactiveVar(currentUserVar);
  const { data, loading, refetch: refetchMeterContractor } = useQuery(
    GET_METER_CONTRACTOR,
    {
      variables: { id: currentUser.contractor?._id },
      notifyOnNetworkStatusChange: false,
    }
  );

  const [createMeterDeliveryRequest, { loading: loadingDeliveryRequest }] = useMutation(
    CREATE_METER_DELIVERY_REQUEST
  );

  const form = useFormik<any>({
    initialValues: {
      contractInfo: {
        contractTitle: "",
        contractNumber: "",
        proposedStartDate: "",
        estimatedProductionCompletionDate: "",
        testAtOwnFacility: false,
      },
      documentationInfo: { },
      metersInfo: {
        testSets: [],
        singlePhaseMeterEnclosures: 0,
        threePhaseMeterEnclosures: 0,
      }
    },
    onSubmit: async (values) => {

      await createMeterDeliveryRequest({
        variables: {
          request: {
            ...values?.contractInfo,
            ...values?.metersInfo,
            testSets: values?.metersInfo?.testSets?.map((testSet: any) => ({
              ...testSet,
              meterBrand: testSet?.meterBrand?._id,
              meterModel: testSet?.meterModel?._id,
              content: testSet.content?.map((item: any) => ({
                stsNumber: item?.stsNumber,
                meterNumber: item?.meterNumber,
              }))
            }))
          },
        },
      }).then(({ data }) => {
        if (data.createMeterDeliveryServiceRequest._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "Meter Delivery Request Successfully",
            })
          );
          navigate({
            to: `/meter-delivery-requests/${data.createMeterDeliveryServiceRequest._id}`,
            search: {},
            replace: true,
          });
          refetch?.();
          form.resetForm();
        } else {
          toast(
            JSON.stringify({ type: "error", title: "Could not create Meter Delivery Request" })
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      title='Create New Meter Delivery Request'
      description='Provide the details to create a new meter delivery request'
      hideActions
      hidePadding
      size="5xl"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loadingDeliveryRequest}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loadingDeliveryRequest ? "Creating Request..." : "Create Request"}
          </button>
        </>
      )}
    >
      <MeterDeliveryRequestForm
        form={form}
        meterContractor={data?.meterContractor}
        refetchMeterContractor={refetchMeterContractor}
      />
    </Modal>
  );
};

export default CreateMeterDeliveryRequestContainer;
